// src/pages/Inbox.tsx
import { useState, useMemo, useEffect, useRef } from 'react'
import { Plus, Sparkles, ChevronDown, ChevronUp, Zap, Clipboard } from 'lucide-react'
import { useTasks } from '../stores/useTasks'
import { TaskCard } from '../components/TaskCard'
import { api } from '../services/api'
import type { Task } from '../types'

interface SectionHeaderProps { title: string; count: number }

function SectionHeader({ title, count }: SectionHeaderProps) {
  return (
    <div className="section-header">
      <span className="label text-muted">{title}</span>
      <span className="badge badge-muted">{count}</span>
    </div>
  )
}

interface InboxProps { onNavigate: (page: string) => void }

export default function InboxPage({ onNavigate }: InboxProps) {
  const { tasks, load, add, loading } = useTasks()
  const [newTaskText, setNewTaskText] = useState('')
  const [showSmartImport, setShowSmartImport] = useState(false)
  const [smartText, setSmartText] = useState('')
  const [importing, setImporting] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { load() }, [])

  const sections = useMemo(() => {
    const pending   = tasks.filter(t => t.type === 'unclassified' && !t.done)
    const signals   = tasks.filter(t => t.type === 'signal'       && !t.done)
    const noise     = tasks.filter(t => t.type === 'noise'        && !t.done)
    const completed = tasks.filter(t => t.done)
    return { pending, signals, noise, completed }
  }, [tasks])

  const handleAddTask = async () => {
    if (!newTaskText.trim()) return
    await add(newTaskText.trim())
    setNewTaskText('')
    inputRef.current?.focus()
  }

  const handleSmartImport = async () => {
    if (!smartText.trim()) return
    setImporting(true)
    try {
      const results = await api.smartImport(smartText)
      for (const r of results) await add(r.text)
      setSmartText('')
    } catch { alert('Smart import failed. Check your connection.') }
    finally { setImporting(false) }
  }

  function renderSection(title: string, taskList: Task[]) {
    if (taskList.length === 0) return null
    return (
      <>
        <SectionHeader title={title} count={taskList.length} />
        {taskList.map(t => <TaskCard key={t.id} task={t} />)}
      </>
    )
  }

  return (
    <div className="page page-top">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--lg)' }}>
        <h1 className="display-md text-primary">Inbox</h1>
        <button
          className="badge badge-accent"
          style={{ cursor: 'pointer', border: '1px solid color-mix(in srgb, var(--accent) 45%, transparent)' }}
          onClick={() => onNavigate('triage')}
        >
          <Zap size={11} /> Triage
        </button>
      </div>

      {/* Quick-add */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sm)', background: 'var(--card-low)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: '0 var(--md)', marginBottom: 'var(--lg)' }}>
        <input
          ref={inputRef}
          id="inbox-quick-add"
          className="input"
          style={{ background: 'transparent', border: 'none', flex: 1, padding: 'var(--md) 0' }}
          value={newTaskText}
          onChange={e => setNewTaskText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAddTask()}
          placeholder="What's the next move?"
        />
        <button className="btn btn-primary btn-icon" style={{ width: 32, height: 32, padding: 0, flexShrink: 0 }} onClick={handleAddTask}>
          <Plus size={18} />
        </button>
      </div>

      {/* Smart Import */}
      <div style={{ marginBottom: 'var(--lg)' }}>
        <button
          onClick={() => setShowSmartImport(s => !s)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', padding: 'var(--sm) 0', cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sm)' }}>
            <Sparkles size={15} color="var(--accent)" />
            <span className="label text-muted">Smart Import</span>
          </div>
          {showSmartImport ? <ChevronUp size={15} color="var(--text-dim)" /> : <ChevronDown size={15} color="var(--text-dim)" />}
        </button>

        {showSmartImport && (
          <div className="animate-fade-slide-up" style={{ marginTop: 'var(--sm)' }}>
            <textarea
              id="smart-import-area"
              className="input textarea"
              value={smartText}
              onChange={e => setSmartText(e.target.value)}
              placeholder="Paste raw notes, brain dump, meeting notes…"
              rows={4}
            />
            <button
              className="btn btn-primary"
              style={{ marginTop: 'var(--sm)' }}
              onClick={handleSmartImport}
              disabled={importing || !smartText.trim()}
            >
              {importing ? <span className="spinner" /> : <Clipboard size={15} />}
              {importing ? 'Importing…' : 'Import'}
            </button>
          </div>
        )}
      </div>

      {/* Task lists */}
      {loading && tasks.length === 0 && (
        <div className="empty-state">
          <span className="spinner" />
          <span className="body-md text-muted">Loading tasks…</span>
        </div>
      )}

      {renderSection('Pending', sections.pending)}
      {renderSection('Signals', sections.signals)}
      {renderSection('Noise Bin', sections.noise)}
      {renderSection('Completed', sections.completed)}

      {!loading && tasks.length === 0 && (
        <div className="empty-state">
          <div style={{ fontSize: 32 }}>📥</div>
          <p className="title text-muted">Inbox zero</p>
          <p className="body-md text-dim">Add your first task above</p>
        </div>
      )}
    </div>
  )
}
